import { Injectable, inject } from "@angular/core";
import { HttpClient, HttpResponse } from "@angular/common/http";
import { Observable, forkJoin, map, switchMap, tap } from "rxjs";
import { MenuNode } from "../archive-view/reordering";

const API = "https://archive-ia.net/api/impression";

@Injectable({ providedIn: "root" })
export class ImpressionService {
  private http = inject(HttpClient);

  /** Récupère le PDF « vide » en mémoire (Blob), sans téléchargement. */
  private vide(): Observable<Blob> {
    return this.http.get(`${API}/vide`, { responseType: "blob" });
  }

  /** Récupère le PDF « videUnit » en mémoire (Blob), sans téléchargement. */
  private videUnit(contenu_unit: string): Observable<Blob> {
    return this.http.post(
      `${API}/vide_unit`,
      { contenu_unit },
      { responseType: "blob" },
    );
  }

  /** Fusionne 3 PDF « vide » titrés 1er / 2ème / 3ème. */
  fusionnerVides(): Observable<HttpResponse<Blob>> {
    const titres = ["1er", "2ème", "3ème"];

    return forkJoin(titres.map(() => this.vide())).pipe(
      map((blobs) => {
        const form = new FormData();
        blobs.forEach((blob, i) => {
          form.append(`documents[${i}][title]`, titres[i]);
          form.append(`documents[${i}][layer]`, "1");
          form.append(`documents[${i}][pdf]`, blob, `vide-${i}.pdf`);
        });
        return form;
      }),
      switchMap((form) =>
        this.http.post(API, form, {
          responseType: "blob",
          observe: "response",
        }),
      ),
    );
  }

  /**
   * Fusionne un PDF « videUnit » par partie (enfant direct d'une racine), titré
   * par son archiveId, avec pour contenu la partie et toute sa descendance.
   */
  fusionnerVidesUnit(nodes: MenuNode[]): Observable<HttpResponse<Blob>> {
    const collectSubtree = (parent: MenuNode): MenuNode[] => [
      parent,
      ...nodes
        .filter((node) => node.parentId === parent.archiveId)
        .flatMap((child) => collectSubtree(child)),
    ];

    const roots = nodes.filter((node) => node.parentId === null);
    const parts = roots.flatMap((root) =>
      nodes.filter((n) => n.parentId === root.archiveId),
    );

    const titres = parts.map((part) => String(part.archiveId));
    const contenus = parts.map((part) =>
      JSON.stringify(collectSubtree(part), null, 2),
    );

    return forkJoin(contenus.map((contenu) => this.videUnit(contenu))).pipe(
      map((blobs) => {
        const form = new FormData();
        blobs.forEach((blob, i) => {
          form.append(`documents[${i}][title]`, titres[i]);
          form.append(`documents[${i}][layer]`, "1");
          form.append(`documents[${i}][pdf]`, blob, `vide-${i}.pdf`);
        });
        return form;
      }),
      switchMap((form) =>
        this.http.post(API, form, {
          responseType: "blob",
          observe: "response",
        }),
      ),
    );
  }

  /** Fusionne 3 PDF « vide » et enregistre le résultat dans les téléchargements. */
  telechargerVides(): Observable<HttpResponse<Blob>> {
    return this.fusionnerVides().pipe(tap((res) => this.enregistrer(res)));
  }

  /** Fusionne un PDF « videUnit » par partie du menu
   *  et enregistre le résultat dans les téléchargements. */
  telechargerVidesUnit(nodes: MenuNode[]): Observable<HttpResponse<Blob>> {
    return this.fusionnerVidesUnit(nodes).pipe(
      tap((res) => this.enregistrer(res)),
    );
  }

  /**
   * Un blob ouvert dans un onglet s'affiche dans la visionneuse : seul un lien
   * `download` déclenche le téléchargement. Le nom vient du Content-Disposition
   * envoyé par Rails (lisible seulement si le front est sur le même domaine ou
   * si CORS expose l'en-tête).
   */
  private enregistrer(res: HttpResponse<Blob>): void {
    const disposition = res.headers.get("Content-Disposition") ?? "";
    const nom = /filename="?([^";]+)"?/.exec(disposition)?.[1] ?? "impression.pdf";

    const url = URL.createObjectURL(res.body!);
    const lien = document.createElement("a");
    lien.href = url;
    lien.download = nom;
    document.body.appendChild(lien);
    lien.click();
    lien.remove();
    URL.revokeObjectURL(url);
  }
}